import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { PageLayout } from '../../components/layout/PageLayout';

export default function LeadsList() {
  const [leads, setLeads] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeads();
  }, []);

  async function fetchLeads() {
    if (!supabase) {
      setLoading(false);
      return;
    }
    
    const { data, error } = await supabase
      .from('paygent_leads')
      .select('*')
      .order('created_at', { ascending: false });
      
    if (error) {
      console.error('Error fetching leads:', error);
    } else {
      setLeads(data || []);
    }
    setLoading(false);
  }

  return (
    <PageLayout
      title="Admin Leads View"
      subtitle="Manage and onboard captured leads."
      badge="INTERNAL ADMIN"
    >
      <div className="max-w-6xl mx-auto">
        <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-xl shadow-blue-900/5 overflow-x-auto">
          <div className="flex justify-between items-center mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <div className="flex items-start gap-3 text-blue-800">
              <i className="ri-shield-check-line text-xl mt-0.5" />
              <div>
                <p className="text-sm font-bold">Secure Admin Portal</p>
                <p className="text-sm">You are viewing protected internal data.</p>
              </div>
            </div>
            <button 
              onClick={() => supabase?.auth.signOut()}
              className="px-4 py-2 bg-white border border-blue-200 text-blue-700 font-medium rounded-lg hover:bg-blue-50 text-sm"
            >
              Sign Out
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading leads...</div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-200 text-sm text-gray-500">
                  <th className="py-4 px-4">Date</th>
                  <th className="py-4 px-4">Business</th>
                  <th className="py-4 px-4">Contact</th>
                  <th className="py-4 px-4">Lead Status</th>
                  <th className="py-4 px-4">NMI Status</th>
                  <th className="py-4 px-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-500">No leads found.</td>
                  </tr>
                ) : (
                  leads.map((lead) => (
                    <tr key={lead.id || lead.contact_email} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-4 text-sm text-gray-600">
                        {new Date(lead.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-4 px-4 font-medium text-gray-900">
                        {lead.business_name}
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-600">
                        {lead.first_name} {lead.last_name}<br/>
                        <span className="text-xs text-gray-400">{lead.contact_email}</span>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-bold rounded-md ${
                          lead.lead_status === 'LEAD_CAPTURED' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {lead.lead_status || 'UNKNOWN'}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-bold rounded-md ${
                          lead.nmi_creation_status === 'CREATED' ? 'bg-green-100 text-green-700' : 
                          lead.nmi_creation_status === 'PENDING_NMI_ONBOARDING' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {lead.nmi_creation_status || 'UNKNOWN'}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <Link to={`/admin/leads/${encodeURIComponent(lead.contact_email)}`} className="text-blue-600 hover:text-blue-800 font-medium text-sm inline-flex items-center gap-1">
                          Manage <i className="ri-arrow-right-line" />
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </PageLayout>
  );
}
